// WARNING: changing DEFAULT values will change how measures are stored in the DB.

import type { Area, Bytes, Distance, Percentage, Time, Volume, Weight } from "@pdfking/structs"
import { Items, RecordCreate, RecordMerge } from "@pdfking/tfunc"

import type { QuantitySpec } from "#public"

import { QuantityType } from "../public/types/QuantityType"

const units = (
  type: QuantityType,
  magnitudes: Record<string, number>
): Record<string, { type: QuantityType; magnitude: number }> =>
  RecordCreate(Items(magnitudes).map(([k, magnitude]) => [k, { type, magnitude }]))

const rescale = (
  spec: QuantitySpec,
  key: (k: string) => string,
  magnitude: (m: number) => number,
  precision: number
): QuantitySpec => ({
  standard_unit: key(spec.standard_unit),
  input_unit: key(spec.input_unit),
  precision,

  units: RecordCreate(
    Items(spec.units).map(([k, u]) => [key(k), { type: u.type, magnitude: magnitude(u.magnitude) }])
  ),
})

// Bare numbers have no spec: parse them with `StringToQuantity(input)` (no
// `spec`), which yields an `exp: 0` (dimensionless) result.

// `perc` (the raw 0..1 fraction) matches Python's `P(perc=...)` standard unit.
export const PERCENTAGE = {
  standard_unit: "perc",
  input_unit: "%",
  precision: 12,
  units: units(QuantityType.INTERNATIONAL, { perc: 1, "%": 0.01 }),
} as QuantitySpec<Percentage>

export const DISTANCE = {
  standard_unit: "um",
  input_unit: "cm",
  precision: 0,

  units: RecordMerge(
    units(QuantityType.INTERNATIONAL, {
      um: 1,
      mm: 1e3,
      cm: 1e4,
      dm: 1e5,
      m: 1e6,
      hm: 1e8,
      km: 1e9,
    }),
    units(QuantityType.IMPERIAL, {
      twip: 635 / 36,
      thou: 25.4, // 127 / 5
      barleycorn: 25400 / 3,
      inch: 25400,
      hand: 101600,
      foot: 304800,
      yard: 914400,
      chain: 20116800,
      furlong: 201168000,
      mile: 1609344000,
      league: 4828032000,
    }),
    units(QuantityType.COMPUTER, { px: 25400 / 72, px_print: 25400 / 300 })
  ),
} as QuantitySpec<Distance>

export const WEIGHT = {
  standard_unit: "ug",
  input_unit: "gr",
  precision: 6,

  units: units(QuantityType.INTERNATIONAL, {
    ug: 1,
    mg: 1e3,
    gr: 1e6,
    kg: 1e9,
    q: 1e11,
    ton: 1e12,
  }),
} as QuantitySpec<Weight>

export const TIME = {
  standard_unit: "sec",
  input_unit: "min",
  precision: 6,

  units: units(QuantityType.INTERNATIONAL, {
    msec: 1e-3,
    sec: 1,
    min: 60,
    hour: 3600, // 60 * 60
    day: 86400, // 60 * 60 * 24
    week: 604800, // 60 * 60 * 24 * 7
  }),
} as QuantitySpec<Time>

export const FILESIZE = {
  standard_unit: "bytes",
  input_unit: "bytes",
  precision: 0,

  units: units(QuantityType.INTERNATIONAL, {
    bytes: 1,
    kb: 1024,
    mb: 1048576, // 1024 ** 2
    gb: 1073741824, // 1024 ** 3
  }),
} as QuantitySpec<Bytes>

export const AREA = rescale(
  DISTANCE,
  k => `sq_${k}`,
  m => m ** 2,
  0
) as QuantitySpec<Area>

export const VOLUME = rescale(
  DISTANCE,
  k => `vol_${k}`,
  m => m ** 3,
  0
) as QuantitySpec<Volume>
